import React, { useCallback, useEffect, useState } from "react";
import { DataTable } from "@/components/DataTable.jsx";
import { DemoBadge } from "@/components/DemoBadge.jsx";
import { BackendZoneBadge, HybridDataStatusBadge } from "@/components/BackendZoneBadge.jsx";
import { useKarateUi } from "@/hooks/useKarateUi.js";
import { useHybridDataStore } from "@/hooks/useHybridDataStore.js";
import {
  apiFetchUsers,
  apiReplaceUserEmail,
  apiForceUserPasswordChange,
} from "@/api/backendUsers054.js";
import { GsdReadableBlock } from "@/coquille-bridge";

function EmailStatusBadge({ status, ui }) {
  const aa = ui.accountAccess;
  const labels = {
    staging_temporary: aa.stagingTemporary,
    official_pending: aa.officialPending,
    official_confirmed: aa.officialConfirmed,
    disabled: aa.emailDisabled,
  };
  return (
    <span className="email-status-badge" data-testid={`email-status-${status}`}>
      {labels[status] || status}
    </span>
  );
}

export function AdminAccountsAccessPanel() {
  const { ui, isEn } = useKarateUi();
  const aa = ui.accountAccess;
  const { store, backendStatus } = useHybridDataStore();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [officialEmail, setOfficialEmail] = useState("");
  const fromBackend = store._dataSource === "backend";

  const loadUsers = useCallback(async () => {
    if (!fromBackend) return;
    setLoading(true);
    setError("");
    try {
      const data = await apiFetchUsers();
      setUsers(data.users || []);
    } catch (err) {
      setError(err.payload?.messageFr || err.message || aa.loadError);
    } finally {
      setLoading(false);
    }
  }, [fromBackend, aa.loadError]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleReplaceEmail = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!selectedId || !officialEmail.trim()) {
      setError(aa.officialNotProvided);
      return;
    }
    try {
      const locale = isEn ? "en" : "fr";
      const res = await apiReplaceUserEmail(selectedId, officialEmail.trim(), locale);
      setSuccess(isEn ? res.messageEn : res.messageFr);
      setOfficialEmail("");
      await loadUsers();
    } catch (err) {
      const payload = err.payload;
      setError(
        (isEn ? payload?.messageEn : payload?.messageFr) || payload?.message || aa.replaceError,
      );
    }
  };

  const handleForcePassword = async (userId) => {
    setError("");
    setSuccess("");
    try {
      await apiForceUserPasswordChange(userId);
      setSuccess(aa.forcePasswordSuccess);
      await loadUsers();
    } catch (err) {
      setError(err.payload?.messageFr || aa.replaceError);
    }
  };

  const columns = [
    { key: "displayName", label: aa.columnName },
    { key: "role", label: aa.columnRole },
    { key: "email", label: aa.columnEmail, render: (r) => (
      <span data-testid={`user-email-${r.id}`}>
        {r.email}
        {r.isStagingEmail ? <DemoBadge label={aa.localTemporary} /> : null}
      </span>
    ) },
    { key: "emailStatus", label: aa.columnEmailStatus, render: (r) => <EmailStatusBadge status={r.emailStatus} ui={ui} /> },
    { key: "mustChangePassword", label: aa.columnMustChange, render: (r) =>
      r.mustChangePassword ? <span data-testid={`must-change-${r.id}`}>{aa.mustChangeBadge}</span> : "—" },
    { key: "lastLoginAt", label: aa.columnLastLogin, render: (r) => (r.lastLoginAt ? new Date(r.lastLoginAt).toLocaleString() : "—") },
    { key: "isLocked", label: aa.columnLocked, render: (r) => (r.isLocked ? aa.accountLocked : "—") },
    { key: "actions", label: aa.columnActions, render: (r) => (
      <button type="button" data-testid={`force-pwd-${r.id}`} onClick={() => handleForcePassword(r.id)}>
        {aa.forcePasswordChange}
      </button>
    ) },
  ];

  return (
    <div className="admin-accounts-access-panel" data-testid="admin-accounts-access-panel">
      <HybridDataStatusBadge dataSource={store._dataSource} backendStatus={backendStatus} testId="admin-comptes-data-badge" />
      {fromBackend ? <BackendZoneBadge zone="auth" /> : null}

      <GsdReadableBlock className="account-access-notice" testId="account-access-no-invent-notice">
        <p>{aa.noInventNotice}</p>
        <p>{aa.stagingTemporaryNotice}</p>
      </GsdReadableBlock>

      {!fromBackend ? (
        <p data-testid="admin-comptes-local-fallback">{aa.localFallback}</p>
      ) : null}
      {loading && fromBackend ? (
        <p>{aa.loading}</p>
      ) : null}
      {fromBackend && !loading ? (
        <DataTable rows={users} columns={columns} testId="admin-users-table" searchFields={["displayName", "email", "role"]} />
      ) : null}

      <form
        className="replace-email-form"
        onSubmit={fromBackend ? handleReplaceEmail : (e) => e.preventDefault()}
        data-testid="replace-official-email-form"
      >
        <h3>{aa.replaceOfficialTitle}</h3>
        <label>
          {aa.selectUser}
          <select
            value={selectedId || ""}
            onChange={(e) => setSelectedId(e.target.value || null)}
            data-testid="replace-email-user-select"
            disabled={!fromBackend || loading}
          >
            <option value="">{aa.selectUserPlaceholder}</option>
            {users.filter((u) => u.isStagingEmail).map((u) => (
              <option key={u.id} value={u.id}>
                {u.displayName} ({u.email})
              </option>
            ))}
          </select>
        </label>
        <label>
          {aa.replaceOfficialLabel}
          <input
            type="email"
            value={officialEmail}
            onChange={(e) => setOfficialEmail(e.target.value)}
            placeholder={aa.replaceOfficialPlaceholder}
            data-testid="replace-official-email-input"
            autoComplete="off"
            disabled={!fromBackend}
          />
        </label>
        <button type="submit" data-testid="replace-official-email-submit" disabled={!fromBackend}>
          {aa.replaceOfficialSubmit}
        </button>
      </form>

      {error ? (
        <p className="form-error" role="alert" data-testid="account-access-error">
          {error}
        </p>
      ) : null}
      {success ? (
        <p className="form-success" role="status" data-testid="account-access-success">
          {success}
        </p>
      ) : null}
      <p data-testid="account-access-no-secrets-notice">{aa.noSecretsNotice}</p>
    </div>
  );
}
